// Shared "fetch from public R2 → register with Strudel → persist to
// IDB" pipeline used by both community-sample import and the user's
// own cloud-sample pull-to-local. The two surfaces only differ in
// (a) the source row's metadata fields and (b) the SampleOrigin
// filename used to label provenance — everything else, including the
// collision-rename rule, is identical.
//
// The blob's storageKey is sha256 over its bytes, so re-pulling a
// sample that's already local (or that exists under a different name
// from a forge derivation) reuses the cached blob row in IDB.

import { cloudSampleUrl } from './playback';
import { registerSampleBlob } from '../samples/register';
import { sha256Hex } from '../samples/storage/hash';
import type { SampleStorage } from '../samples/storage/idb';
import type { License, Sample } from '../samples/types';

export type ImportSource = {
  /** Server-side sample id; used for collision suffixes. */
  id: string;
  name: string;
  durationMs: number;
  tags: string[];
  license: License;
  r2Key: string;
};

export type ImportCloudBlobInput = {
  source: ImportSource;
  /** Provenance label written into the local Sample row's
   *  `origin.filename`. e.g. "community:<id>" or "mine:<id>". */
  filenameOrigin: string;
  ctx: AudioContext;
  storage: SampleStorage;
};

export type ImportCloudBlobResult = {
  sample: Sample;
};

export async function importCloudBlobToLocal(
  input: ImportCloudBlobInput,
): Promise<ImportCloudBlobResult> {
  const { source, filenameOrigin, ctx, storage } = input;

  const url = cloudSampleUrl(source.r2Key);
  if (!url) throw new Error('VITE_R2_PUBLIC_URL not configured');

  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(
      `cloud sample fetch failed (${res.status}): ${res.statusText}`,
    );
  }
  const blob = await res.blob();

  const existing = await storage.listSamples();
  const taken = new Set(existing.map((s) => s.name));
  const name = pickUniqueName(source.name, source.id, taken);

  const buffer = await registerSampleBlob(ctx, blob, { name });

  const storageKey = await sha256Hex(await blob.arrayBuffer());
  await storage.putBlob(storageKey, blob);

  const sample: Sample = {
    id: crypto.randomUUID(),
    storageKey,
    name,
    durationMs: source.durationMs,
    sampleRate: buffer.sampleRate,
    channels: buffer.numberOfChannels === 2 ? 2 : 1,
    tags: source.tags,
    license: source.license,
    origin: { kind: 'import', filename: filenameOrigin },
    createdAt: Date.now(),
  };
  await storage.putSample(sample);

  return { sample };
}

/** Picks a local sample name that doesn't collide with any of `taken`.
 *  First choice is the cloud name as-is; on collision, suffix with the
 *  first 4 chars of the cloud id (matches user-readable expectations
 *  like `kick_1111`); on a double collision, walk the numeric suffix
 *  loop. The dual-collision path is exercised exclusively by the unit
 *  test today — in practice the 4-char suffix lands a unique name. */
export function pickUniqueName(
  cloudName: string,
  cloudId: string,
  taken: Set<string>,
): string {
  if (!taken.has(cloudName)) return cloudName;
  const shortId = cloudId.replace(/-/g, '').slice(0, 4);
  const candidate = `${cloudName}_${shortId}`;
  if (!taken.has(candidate)) return candidate;
  let i = 2;
  while (taken.has(`${candidate}_${i}`)) i++;
  return `${candidate}_${i}`;
}
